/* ============================================================================
   PARHELIK — Wishlist (framework-free)

   Ports minimog's wishlist.js behaviour onto the Parhelik design system.

   Responsibilities
     1. Persist a list of product HANDLES in localStorage (de-duplicated,
        most-recent first preserved), matching minimog's storage model so the
        feature behaves identically.
     2. Wire every [data-ph-wishlist-toggle] (on product cards and the PDP):
        click toggles the saved state, flips the heart's pressed state +
        accessible label, and keeps every toggle for the same product in sync.
     3. Keep every [data-ph-wishlist-count] badge in sync (text = count; the
        element is hidden when the list is empty). Toggles body.ph-has-wishlist.
     4. On the wishlist PAGE ([data-ph-wishlist-page]) read the saved handles,
        fetch THIS section rendered server-side with those products (search
        section-rendering API), inject the grid, and add a per-card remove
        control. Empty state shown when nothing is saved / available.

   After injecting cards, dispatches shopify:section:load so parhelik-global.js
   (reveal/swatches) and parhelik-product.js (quick-add/variants) re-wire the new
   nodes. Re-inits on shopify:section:load (theme editor). Guarded so double
   loading the asset is harmless.
   ========================================================================== */
(function () {
  'use strict';

  if (window.PHWishlist && window.PHWishlist._installed) return;

  var STORAGE_KEY = 'parhelik-wishlist';
  var BODY_FLAG = 'ph-has-wishlist';
  var ACTIVE_CLASS = 'is-in-wishlist';

  var TOGGLE_SEL = '[data-ph-wishlist-toggle]';
  var COUNT_SEL = '[data-ph-wishlist-count]';
  var PAGE_SEL = '[data-ph-wishlist-page]';
  var REMOVE_SEL = '[data-ph-wishlist-remove]';
  var CARD_SEL = '.ph-product-card';

  /* ---------------------------- localStorage ---------------------------- */
  function readHandles() {
    try {
      var raw = window.localStorage.getItem(STORAGE_KEY);
      var parsed = raw ? JSON.parse(raw) : [];
      if (!Array.isArray(parsed)) return [];
      var seen = {};
      var out = [];
      for (var i = 0; i < parsed.length; i++) {
        var h = String(parsed[i] || '').trim();
        if (h && !seen[h]) { seen[h] = 1; out.push(h); }
      }
      return out;
    } catch (e) {
      return [];
    }
  }

  function writeHandles(handles) {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(handles));
    } catch (e) {
      /* storage unavailable / quota — fail silently */
    }
  }

  function has(handle) {
    return readHandles().indexOf(handle) !== -1;
  }

  function add(handle) {
    if (!handle) return;
    var handles = readHandles();
    if (handles.indexOf(handle) === -1) {
      handles.push(handle);
      writeHandles(handles);
    }
  }

  function remove(handle) {
    if (!handle) return;
    var handles = readHandles().filter(function (h) { return h !== handle; });
    writeHandles(handles);
  }

  function toggle(handle) {
    if (!handle) return false;
    if (has(handle)) { remove(handle); return false; }
    add(handle);
    return true;
  }

  /* --------------------------- identity helpers ------------------------- */
  // Resolve a product handle from a toggle / card element.
  function handleFrom(el) {
    if (!el) return '';
    var h =
      el.getAttribute('data-ph-wishlist-handle') ||
      el.getAttribute('data-product-handle') ||
      el.getAttribute('data-handle') || '';
    if (h) return h;
    // Walk up to a card / product root that carries the handle.
    var host = el.closest && el.closest('[data-product-handle],[data-ph-product]');
    if (host) {
      h = host.getAttribute('data-product-handle') || host.getAttribute('data-handle') || '';
      if (h) return h;
    }
    // Last resort: parse a /products/{handle} href.
    var href = el.getAttribute && el.getAttribute('href');
    var m = href && href.match(/\/products\/([^/?#]+)/);
    return m ? m[1] : '';
  }

  /* ------------------------------ toggle UI ----------------------------- */
  function applyToggleState(btn, active) {
    if (!btn) return;
    btn.classList.toggle(ACTIVE_CLASS, active);
    btn.setAttribute('aria-pressed', active ? 'true' : 'false');

    // Swap accessible label / tooltip between add / remove copy when provided.
    var addLabel = btn.getAttribute('data-label-add');
    var removeLabel = btn.getAttribute('data-label-remove');
    if (addLabel || removeLabel) {
      var label = active ? (removeLabel || addLabel) : (addLabel || removeLabel);
      if (label) {
        btn.setAttribute('aria-label', label);
        if (btn.hasAttribute('title')) btn.setAttribute('title', label);
      }
    }
    // Optional visible text node.
    var text = btn.querySelector && btn.querySelector('[data-ph-wishlist-text]');
    if (text && (addLabel || removeLabel)) {
      text.textContent = active ? (removeLabel || addLabel) : (addLabel || removeLabel);
    }
  }

  // Reflect the stored state onto every toggle currently in the DOM.
  function syncToggles(scope) {
    scope = scope || document;
    var saved = readHandles();
    var nodes = scope.querySelectorAll ? scope.querySelectorAll(TOGGLE_SEL) : [];
    for (var i = 0; i < nodes.length; i++) {
      var btn = nodes[i];
      var handle = handleFrom(btn);
      applyToggleState(btn, handle ? saved.indexOf(handle) !== -1 : false);
    }
  }

  // Keep all toggles for one product in lock-step after a single click.
  function syncHandle(handle, active) {
    if (!handle) return;
    var nodes = document.querySelectorAll(TOGGLE_SEL);
    for (var i = 0; i < nodes.length; i++) {
      if (handleFrom(nodes[i]) === handle) applyToggleState(nodes[i], active);
    }
  }

  /* ------------------------------- count -------------------------------- */
  function updateCount() {
    var size = readHandles().length;
    var nodes = document.querySelectorAll(COUNT_SEL);
    for (var i = 0; i < nodes.length; i++) {
      var el = nodes[i];
      el.textContent = String(size);
      el.toggleAttribute ? el.toggleAttribute('hidden', size < 1) : (size < 1 ? el.setAttribute('hidden', 'hidden') : el.removeAttribute('hidden'));
      el.classList.toggle('is-empty', size < 1);
    }
    document.body.classList.toggle(BODY_FLAG, size > 0);
  }

  /* --------------------------- toggle handling -------------------------- */
  function onClick(e) {
    var toggleEl = e.target.closest && e.target.closest(TOGGLE_SEL);
    if (toggleEl) {
      e.preventDefault();
      var handle = handleFrom(toggleEl);
      if (!handle) return;
      var active = toggle(handle);
      syncHandle(handle, active);
      updateCount();
      document.dispatchEvent(new CustomEvent('ph:wishlist:change', {
        bubbles: true,
        detail: { handle: handle, active: active, count: readHandles().length }
      }));
      return;
    }

    // Remove control on the wishlist page.
    var removeEl = e.target.closest && e.target.closest(REMOVE_SEL);
    if (removeEl) {
      e.preventDefault();
      var rHandle = handleFrom(removeEl);
      if (!rHandle) return;
      remove(rHandle);
      syncHandle(rHandle, false);
      updateCount();
      var page = removeEl.closest(PAGE_SEL);
      var cell = removeEl.closest('[data-ph-wishlist-cell]');
      if (cell && cell.parentNode) cell.parentNode.removeChild(cell);
      if (page && readHandles().length === 0) showEmpty(page);
      document.dispatchEvent(new CustomEvent('ph:wishlist:change', {
        bubbles: true,
        detail: { handle: rHandle, active: false, count: readHandles().length }
      }));
    }
  }

  /* ------------------------- wishlist page render ----------------------- */
  function root() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  function showEmpty(page) {
    if (!page) return;
    var grid = page.querySelector('[data-ph-wishlist-grid]');
    var empty = page.querySelector('[data-ph-wishlist-empty]');
    var loading = page.querySelector('[data-ph-wishlist-loading]');
    if (loading) loading.setAttribute('hidden', 'hidden');
    if (grid) grid.setAttribute('hidden', 'hidden');
    if (empty) empty.removeAttribute('hidden');
  }

  function showGrid(page) {
    if (!page) return;
    var grid = page.querySelector('[data-ph-wishlist-grid]');
    var empty = page.querySelector('[data-ph-wishlist-empty]');
    var loading = page.querySelector('[data-ph-wishlist-loading]');
    if (loading) loading.setAttribute('hidden', 'hidden');
    if (empty) empty.setAttribute('hidden', 'hidden');
    if (grid) grid.removeAttribute('hidden');
  }

  // Build "handle:a OR handle:b" — Shopify search indexes the product handle.
  function buildQuery(handles) {
    return handles
      .map(function (h) { return 'handle:' + h; })
      .join(' OR ');
  }

  function removeButton(handle, label) {
    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'ph-wishlist__remove';
    btn.setAttribute('data-ph-wishlist-remove', '');
    btn.setAttribute('data-product-handle', handle);
    btn.setAttribute('aria-label', label || 'Remove from wishlist');
    btn.setAttribute('title', label || 'Remove from wishlist');
    btn.innerHTML =
      '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
      '<path d="M5 5l14 14M19 5L5 19"></path></svg>';
    return btn;
  }

  function renderPage(page) {
    if (!page || page.dataset.phWlLoaded === '1') return;
    page.dataset.phWlLoaded = '1';

    var handles = readHandles();
    if (!handles.length) { showEmpty(page); return; }

    var base = page.getAttribute('data-fetch-url');
    if (!base) { showEmpty(page); return; }

    var url = base + encodeURIComponent(buildQuery(handles));

    fetch(url, { headers: { Accept: 'text/html' } })
      .then(function (r) {
        if (!r.ok) throw new Error('wishlist http ' + r.status);
        return r.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var fresh = doc.querySelector('[data-ph-wishlist-results]');
        var grid = page.querySelector('[data-ph-wishlist-grid]');
        if (!grid) { showEmpty(page); return; }

        // Index returned cards by handle so we can render in saved order.
        var byHandle = {};
        if (fresh) {
          var cards = fresh.querySelectorAll(CARD_SEL);
          for (var i = 0; i < cards.length; i++) {
            var h = cards[i].getAttribute('data-product-handle');
            if (h) byHandle[h] = cards[i];
          }
        }

        var removeLabel = page.getAttribute('data-remove-label') || 'Remove from wishlist';
        var rendered = 0;
        grid.innerHTML = '';
        for (var j = 0; j < handles.length; j++) {
          var card = byHandle[handles[j]];
          if (!card) continue; // product no longer exists / unavailable -> skip
          var cell = document.createElement('div');
          cell.className = 'ph-wishlist__cell';
          cell.setAttribute('data-ph-wishlist-cell', '');
          cell.setAttribute('data-product-handle', handles[j]);
          cell.appendChild(document.importNode(card, true));
          cell.appendChild(removeButton(handles[j], removeLabel));
          grid.appendChild(cell);
          rendered++;
        }

        if (rendered === 0) { showEmpty(page); return; }
        showGrid(page);
        rewire(page);
        syncToggles(page);
        updateCount();
      })
      .catch(function () {
        showEmpty(page);
      });
  }

  // Let the global engines wire the freshly injected cards.
  function rewire(scope) {
    if (window.PHProduct && typeof window.PHProduct.init === 'function') {
      try { window.PHProduct.init(scope); } catch (e) { /* noop */ }
    }
    if (window.Parhelik && typeof window.Parhelik.observeAll === 'function') {
      try { window.Parhelik.observeAll(scope); } catch (e) { /* noop */ }
    }
    try {
      document.dispatchEvent(new CustomEvent('shopify:section:load', {
        detail: { sectionId: scope.id }, bubbles: false
      }));
    } catch (e) { /* noop */ }
  }

  /* ------------------------------- boot --------------------------------- */
  function boot(scope) {
    scope = scope || document;
    syncToggles(scope);
    updateCount();
    var pages = scope.querySelectorAll ? scope.querySelectorAll(PAGE_SEL) : [];
    for (var i = 0; i < pages.length; i++) renderPage(pages[i]);
  }

  document.addEventListener('click', onClick);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { boot(document); });
  } else {
    boot(document);
  }

  // Theme editor / re-render: re-sync toggles + re-fetch the page grid.
  document.addEventListener('shopify:section:load', function (e) {
    var target = (e && e.target && e.target.querySelector) ? e.target : document;
    syncToggles(target);
    updateCount();
    var page = target.querySelector ? target.querySelector(PAGE_SEL) : null;
    if (target.matches && target.matches(PAGE_SEL)) page = target;
    if (page) { page.dataset.phWlLoaded = ''; renderPage(page); }
  });

  /* ------------------------------ public API ---------------------------- */
  window.PHWishlist = {
    _installed: true,
    read: readHandles,
    has: has,
    add: function (h) { add(h); syncHandle(h, true); updateCount(); },
    remove: function (h) { remove(h); syncHandle(h, false); updateCount(); },
    toggle: function (h) { var a = toggle(h); syncHandle(h, a); updateCount(); return a; },
    count: function () { return readHandles().length; },
    refresh: function (scope) {
      var page = (scope && scope.querySelector && scope.querySelector(PAGE_SEL)) || document.querySelector(PAGE_SEL);
      if (page) { page.dataset.phWlLoaded = ''; renderPage(page); }
      syncToggles(scope || document);
      updateCount();
    }
  };
})();
